/**
 * Frontend client for the Vault API (server/modules/vault + embeds). Injects
 * the auth token + active-workspace header from localStorage (same keys the
 * AuthContext writes), so every call is tenant-scoped.
 */

const API_BASE = "/api/v1";

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = localStorage.getItem("auth_token");
  const ws = localStorage.getItem("active_workspace_id");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(ws ? { "X-Workspace-Id": ws } : {}),
    ...extra,
  };
}

async function api<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: authHeaders(options.headers as Record<string, string>),
  });
  const json = (await res.json()) as ApiEnvelope<T>;
  if (!res.ok || !json.success) throw new Error(json.message || "Request failed");
  return json.data;
}

// ─────────────────────────── Types ──────────────────────────────────────────

export type ModelStatus = "uploading" | "converting" | "ready" | "error";

export interface VaultFolder {
  _id: string;
  name: string;
  parentId: string | null;
  path: string;
  createdAt: string;
  /** Direct non-deleted models + subfolders (populated by the children listing). */
  childCount?: number;
}

export interface ModelLayer {
  name: string;
  type: "metal" | "gem" | "other";
}

export interface VaultModel {
  _id: string;
  name: string;
  description?: string;
  folderId: string | null;
  status: ModelStatus;
  statusMessage?: string;
  format: string;
  size: number;
  glbSize?: number;
  posterKey?: string;
  posterUrl?: string | null;
  category?: string;
  tags: string[];
  layers: ModelLayer[];
  config?: Record<string, unknown>;
  /** v65 — the uploaded file carries its own whole scene (a "Ring + scene"
   *  .glb). Set by the server on processing; absent on older models. */
  embeddedScene?: boolean;
  visibility: "private" | "shared" | "public";
  starredBy?: string[];
  createdAt: string;
  updatedAt: string;
}

// ─────────────────────────── Folders ────────────────────────────────────────

export const listChildren = (parentId: string | null) =>
  api<{ folders: VaultFolder[]; models: VaultModel[] }>(
    `/vault/folders/children${parentId ? `?parentId=${parentId}` : ""}`,
  );

export const createFolder = (name: string, parentId: string | null) =>
  api<VaultFolder>("/vault/folders", {
    method: "POST",
    body: JSON.stringify({ name, parentId }),
  });

export const renameFolder = (id: string, name: string) =>
  api<VaultFolder>(`/vault/folders/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });

export const deleteFolder = (id: string) =>
  api<{ id: string }>(`/vault/folders/${id}`, { method: "DELETE" });

// ─────────────────────────── Models ─────────────────────────────────────────

export const listModels = (
  folderId: string | null,
  opts: { trash?: boolean; recent?: boolean; configurators?: boolean; starred?: boolean; shared?: boolean } = {},
) => {
  const p = new URLSearchParams();
  if (opts.trash) p.set("trash", "true");
  else if (opts.recent) p.set("recent", "true");
  else if (opts.configurators) p.set("configurators", "true");
  else if (opts.starred) p.set("starred", "true");
  else if (opts.shared) p.set("shared", "true");
  else p.set("folderId", folderId ?? "");
  return api<VaultModel[]>(`/vault/models?${p.toString()}`);
};

export const getModel = (id: string) => api<VaultModel>(`/vault/models/${id}`);

// The server returns a RELATIVE signed path (/api/v1/files/glb/...). The viewer
// iframe is a different origin, so it can't resolve a relative URL against OUR
// origin — absolutize it against the app origin before handing it over.
export const getModelGlbUrl = async (id: string): Promise<{ url: string }> => {
  const { url } = await api<{ url: string }>(`/vault/models/${id}/glb-url`);
  return { url: /^https?:\/\//.test(url) ? url : new URL(url, window.location.origin).href };
};

export const updateModel = (id: string, patch: Partial<VaultModel> & { folderId?: string | null }) =>
  api<VaultModel>(`/vault/models/${id}`, { method: "PATCH", body: JSON.stringify(patch) });

export const saveModelConfig = (id: string, config: Record<string, unknown>) =>
  api<VaultModel>(`/vault/models/${id}/config`, { method: "PUT", body: JSON.stringify({ config }) });

export const saveModelPoster = (id: string, dataUrl: string) =>
  api<{ posterUrl: string }>(`/vault/models/${id}/poster`, {
    method: "POST",
    body: JSON.stringify({ dataUrl }),
  });

export const starModel = (id: string) =>
  api<{ starred: boolean; id: string }>(`/vault/models/${id}/star`, { method: "POST" });

export const deleteModel = (id: string) =>
  api<{ id: string }>(`/vault/models/${id}`, { method: "DELETE" });

export const restoreModel = (id: string) =>
  api<VaultModel>(`/vault/models/${id}/restore`, { method: "POST" });

/** Permanently delete a trashed model + its S3 assets (no recovery). */
export const purgeModel = (id: string) =>
  api<{ id: string }>(`/vault/models/${id}/purge`, { method: "DELETE" });

/**
 * Create a model record + presigned PUT, upload the source bytes, then trigger
 * server-side conversion. Returns the ready (or error) model.
 */
export async function uploadModel(
  file: File,
  folderId: string | null,
  onStage?: (stage: "uploading" | "converting") => void,
): Promise<VaultModel> {
  const { model } = await api<{ model: VaultModel; uploadUrl: string }>("/vault/models", {
    method: "POST",
    body: JSON.stringify({
      name: file.name.replace(/\.[^.]+$/, ""),
      filename: file.name,
      contentType: file.type || "application/octet-stream",
      size: file.size,
      folderId,
    }),
  });

  onStage?.("uploading");
  // Push the source THROUGH the server as RAW binary (not base64) so large CAD
  // files (.3dm can be 40–100 MB+) upload without inflating past body limits. We
  // relay via the server because the shared default bucket has no browser-origin
  // CORS policy — a direct browser→S3 PUT fails with "Failed to fetch".
  const res = await fetch(`${API_BASE}/vault/models/${model._id}/source`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": file.type || "application/octet-stream" }),
    body: file,
  });
  if (!res.ok) throw new Error("Upload to storage failed");

  onStage?.("converting");
  return api<VaultModel>(`/vault/models/${model._id}/process`, { method: "POST" });
}

// ─────────────────────────── Embeds ─────────────────────────────────────────

/** White-label / viewer settings stored on an embed (applied host-side in the
 *  public embed runtime — no viewer contract change). Persisted in Embed.options. */
export interface EmbedOptions {
  /** Scene snapshot captured when the share link was made. Wins over the
   *  model's saved config in resolveEmbed, so a link shows what was on screen. */
  vjson?: string;
  autoRotate?: boolean;
  autoRotateSpeed?: number;
  showTitle?: boolean;
  background?: { type: "transparent" | "color"; color?: string };
  logo?: {
    dataUrl?: string;
    show?: boolean;
    fit?: "contain" | "cover";
    position?: "left" | "center" | "right";
  };
  removeBacklink?: boolean;
  loaderStyle?: "spinner" | "logo" | "progress";
  allowDownload?: boolean;
  responsive?: boolean;
  width?: number;
  height?: number;
}

export const EMBED_DEFAULTS: Required<Pick<EmbedOptions,
  "autoRotate" | "autoRotateSpeed" | "showTitle" | "removeBacklink" | "loaderStyle" | "allowDownload" | "responsive" | "width" | "height">> = {
  autoRotate: false,
  autoRotateSpeed: 1,
  showTitle: true,
  removeBacklink: false,
  loaderStyle: "spinner",
  allowDownload: false,
  responsive: true,
  width: 1280,
  height: 720,
};

export interface VaultEmbed {
  _id: string;
  modelId: string;
  token: string;
  allowedDomains: string[];
  options: EmbedOptions;
  viewCount: number;
  downloadCount: number;
  access: "view" | "download" | "comment" | "approve";
  expiresAt: string | null;
  clientName?: string;
  createdAt: string;
}

export interface LinksSummary {
  activeLinks: number;
  views30d: number;
  downloads30d: number;
  expiringThisWeek: number;
}

interface EmbedResult {
  embed: VaultEmbed;
  embedUrl: string;
  snippet: string;
}

export const listEmbeds = (modelId: string) =>
  api<VaultEmbed[]>(`/embeds?modelId=${modelId}`);

export const createEmbed = (modelId: string, allowedDomains: string[] = [], options?: EmbedOptions) =>
  api<EmbedResult>("/embeds", {
    method: "POST",
    body: JSON.stringify({ modelId, allowedDomains, ...(options ? { options } : {}) }),
  });

export const updateEmbed = (
  id: string,
  allowedDomains: string[],
  options?: EmbedOptions,
  share?: { access?: string; expiresAt?: string | null },
) =>
  api<EmbedResult>(`/embeds/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ allowedDomains, ...(options ? { options } : {}), ...(share ?? {}) }),
  });

export const deleteEmbed = (id: string) =>
  api<{ id: string }>(`/embeds/${id}`, { method: "DELETE" });

export const getLinksSummary = () => api<LinksSummary>("/embeds/summary");

// ─────────────────────────── Showcase (public) ──────────────────────────────

export interface ShowcaseModel {
  id: string;
  name: string;
  category: string | null;
  tags: string[];
  posterUrl: string | null;
  configurable: boolean;
}
export interface ShowcaseData {
  workspace: { name: string; slug: string; logo: string | null };
  models: ShowcaseModel[];
}

export const getShowcase = (slug: string) => api<ShowcaseData>(`/showcase/${slug}`);

// ─────────────────────────── Billing ────────────────────────────────────────

export interface VaultPlan {
  _id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  features: string[];
  popular: boolean;
  storageEnabled: boolean;
  maxMembers: number;
  limits: { maxModels: number; maxConfigurators: number; maxDomains: number; embedViews: number };
  stripePriceIdMonthly?: string;
}
export interface BillingState {
  plan: VaultPlan | null;
  billingType: "stripe" | "manual";
  subscription: { status: string; currentPeriodEnd: number | null } | null;
}

// ─────────────────────────── Custom domains ─────────────────────────────────

export interface CustomDomainDns {
  type: "A" | "CNAME";
  name: string;
  value: string;
  note: string;
}
export interface CustomDomain {
  _id: string;
  domain: string;
  status: "pending" | "active";
  brandTitle?: string;
  brandFavicon?: string;
  lastCheckedAt?: string;
  createdAt: string;
  dns: CustomDomainDns;
}

/** White-label branding served to a top-level embed page by its host. */
export interface EmbedBranding {
  whiteLabel: boolean;
  title: string | null;
  favicon: string | null;
  byline: string;
}

export const listDomains = () =>
  api<{ domains: CustomDomain[]; proxyHost: string; proxyIp: string }>("/domains");

/** Public — no auth; resolves branding for the host the embed is loaded on. */
export const getEmbedBranding = (host: string) =>
  fetch(`${API_BASE}/domains/branding?host=${encodeURIComponent(host)}`)
    .then((r) => r.json())
    .then((j) => (j?.success ? (j.data as EmbedBranding) : null))
    .catch(() => null);

// ─────────────────────────── BYO Storage ────────────────────────────────────

export type ProvisionStep =
  | "validating"
  | "creating-bucket"
  | "securing-bucket"
  | "configuring-cors"
  | "creating-cdn"
  | "finalizing"
  | "ready"
  | "error";

export interface StorageProvisioning {
  step: ProvisionStep;
  message: string;
  error: string | null;
  cdnStatus: "deploying" | "deployed" | null;
}

export interface StorageStatus {
  enabled: boolean;
  region: string | null;
  bucket: string | null;
  cloudfrontDomain: string | null;
  accessKeyId: string | null;
  accountId?: string;
  provisioning: StorageProvisioning | null;
}

export interface StorageUsage {
  usedBytes: number;
  totalBytes: number;
  connected: boolean;
  modelCount: number;
  breakdown: { masters: number; renders: number; docs: number };
}

export const getStorage = () => api<StorageStatus>("/storage");
export const getStorageUsage = () => api<StorageUsage>("/storage/usage");
export const connectStorage = (accessKeyId: string, secretAccessKey: string, region: string) =>
  api<StorageStatus>("/storage/connect", {
    method: "POST",
    body: JSON.stringify({ accessKeyId, secretAccessKey, region }),
  });
export const disconnectStorage = () =>
  api<{ enabled: boolean }>("/storage/disconnect", { method: "POST" });
export const addDomain = (domain: string, branding?: { brandTitle?: string; brandFavicon?: string }) =>
  api<CustomDomain>("/domains", { method: "POST", body: JSON.stringify({ domain, ...branding }) });
export const updateDomainBranding = (id: string, branding: { brandTitle?: string; brandFavicon?: string | null }) =>
  api<CustomDomain>(`/domains/${id}/branding`, { method: "PATCH", body: JSON.stringify(branding) });
export const verifyDomain = (id: string) =>
  api<CustomDomain>(`/domains/${id}/verify`, { method: "POST" });
export const deleteDomain = (id: string) =>
  api<{ id: string }>(`/domains/${id}`, { method: "DELETE" });

// ─────────────────────────── Billing ────────────────────────────────────────

export const listPlans = () => api<VaultPlan[]>("/billing/plans");
export const getBilling = () => api<BillingState>("/billing");
export const createCheckout = (planId: string) =>
  api<{ checkoutUrl: string }>("/billing/checkout", { method: "POST", body: JSON.stringify({ planId }) });
export const createPortal = () => api<{ portalUrl: string }>("/billing/portal", { method: "POST" });

export const resolveShowcaseModel = (slug: string, modelId: string) =>
  api<{
    viewerOrigin: string;
    glbUrl: string;
    format: string;
    posterUrl: string | null;
    config: { vjson?: string; configurator?: unknown } | null;
    name: string;
  }>(`/showcase/${slug}/${modelId}`);
